import { Controller } from "@hotwired/stimulus"
import gsap from "gsap"
import ScrollTrigger from "gsap/ScrollTrigger"

gsap.registerPlugin(ScrollTrigger)

export default class extends Controller {
  connect() {
    this.initAnimations()
    this.setupTurboNavigation()
  }

  setupTurboNavigation() {
    this.turboListener = () => {
      ScrollTrigger.getAll().forEach(trigger => trigger.kill())
      requestAnimationFrame(() => {
        this.initAnimations()
        ScrollTrigger.refresh()
      })
    }
    document.addEventListener('turbo:frame-load', this.turboListener)
  }

  initAnimations() {
    this.animateHeroSection()
    this.animatePartnersSection()
    this.animateTestimonialSection()
  }

  animateHeroSection() {
    const hero = document.querySelector('#hero')
    if (!hero) return

    const title = hero.querySelector('h1')
    const titleLine1 = title?.querySelector('span:first-child')
    const titleLine2 = title?.querySelector('span:nth-child(2)')
    const mission = hero.querySelector('p')
    const logo = hero.querySelector('img')

    // Timeline for hero section
    const tl = gsap.timeline({ defaults: { ease: "power3.out" } })

    // Animate first line of title
    if (titleLine1) {
      tl.from(titleLine1, {
        y: 60,
        opacity: 0,
        duration: 1,
        ease: "back.out(1.4)"
      })
    }

    // Animate second line with gradient effect
    if (titleLine2) {
      tl.from(titleLine2, {
        y: 60,
        opacity: 0,
        scale: 0.8,
        duration: 1,
        ease: "back.out(1.7)"
      }, "-=0.6")

      // Add subtle pulsing glow effect
      gsap.to(titleLine2, {
        textShadow: "0 0 20px rgba(34, 197, 94, 0.5)",
        duration: 2,
        repeat: -1,
        yoyo: true,
        ease: "sine.inOut"
      })
    }

    // Animate mission text
    if (mission) {
      tl.from(mission, {
        y: 40,
        opacity: 0,
        duration: 0.8
      }, "-=0.4")
    }

    // Animate logo
    if (logo) {
      tl.from(logo, {
        scale: 0,
        opacity: 0,
        duration: 1.2,
        ease: "elastic.out(1, 0.5)"
      }, "-=0.8")

      // Add floating animation to logo
      gsap.to(logo, {
        y: -15,
        duration: 3,
        repeat: -1,
        yoyo: true,
        ease: "sine.inOut"
      })

      // Add gentle animation
      gsap.to(logo, {
        duration: 4,
        repeat: -1,
        yoyo: true,
        ease: "sine.inOut"
      })
    }

    // Animate background gradient
    gsap.to(hero, {
      background: "linear-gradient(135deg, #224075 0%, #030a21 100%)",
      duration: 10,
      repeat: -1,
      yoyo: true,
      ease: "sine.inOut"
    })
  }

  animatePartnersSection() {
    const section = document.querySelector('section[aria-labelledby="partners-heading"]')
    if (!section) return

    const heading = section.querySelector('h2')
    const description = section.querySelector('p')
    const partnerImages = section.querySelectorAll('img, a')

    // Animate heading
    if (heading) {
      gsap.from(heading, {
        scrollTrigger: {
          trigger: heading,
          start: "top 80%",
          toggleActions: "play none none reverse"
        },
        x: -100,
        opacity: 0,
        duration: 1,
        ease: "power3.out"
      })
    }

    // Animate description
    if (description) {
      gsap.from(description, {
        scrollTrigger: {
          trigger: description,
          start: "top 85%",
          toggleActions: "play none none reverse"
        },
        x: -80,
        opacity: 0,
        duration: 0.8,
        delay: 0.2,
        ease: "power2.out"
      })
    }

    // Animate partner logos
    partnerImages.forEach((img, index) => {
      gsap.from(img, {
        scrollTrigger: {
          trigger: img,
          start: "top 85%",
          toggleActions: "play none none reverse"
        },
        scale: 0,
        opacity: 0,
        duration: 1,
        delay: index * 0.2,
        ease: "back.out(1.7)"
      })

      // Add hover effect
      img.addEventListener('mouseenter', () => {
        gsap.to(img, {
          scale: 1.1,
          duration: 0.3,
          ease: "power2.out"
        })
      })

      img.addEventListener('mouseleave', () => {
        gsap.to(img, {
          scale: 1,
          duration: 0.3,
          ease: "power2.out"
        })
      })
    })
  }

  animateTestimonialSection() {
    const section = document.querySelector('section[aria-labelledby="testimonial-heading"]')
    if (!section) return

    const logo = section.querySelector('a img')
    const blockquote = section.querySelector('blockquote p')
    const figcaption = section.querySelector('figcaption')

    // Animate background gradient effect
    const bgElement = section.querySelector('.absolute.inset-0')
    if (bgElement) {
      gsap.from(bgElement, {
        scrollTrigger: {
          trigger: section,
          start: "top 80%",
          toggleActions: "play none none reverse"
        },
        opacity: 0,
        scale: 1.5,
        duration: 2,
        ease: "power2.out"
      })
    }

    // Animate skewed background
    const skewedBg = section.querySelector('.absolute.inset-y-0')
    if (skewedBg) {
      gsap.from(skewedBg, {
        scrollTrigger: {
          trigger: section,
          start: "top 80%",
          toggleActions: "play none none reverse"
        },
        x: -100,
        opacity: 0,
        duration: 1.5,
        ease: "power3.out"
      })
    }

    // Animate Emargence logo
    if (logo) {
      gsap.from(logo, {
        scrollTrigger: {
          trigger: logo,
          start: "top 85%",
          toggleActions: "play none none reverse"
        },
        y: -50,
        opacity: 0,
        scale: 0.5,
        duration: 1,
        ease: "back.out(1.4)"
      })
    }

    // Animate testimonial text
    if (blockquote) {
      gsap.from(blockquote, {
        scrollTrigger: {
          trigger: blockquote,
          start: "top 85%",
          toggleActions: "play none none reverse"
        },
        y: 40,
        opacity: 0,
        duration: 1.5,
        ease: "power2.out",
        delay: 0.5
      })
    }

    // Animate author info
    if (figcaption) {
      const authorElements = figcaption.querySelectorAll('div, a, svg')

      gsap.from(authorElements, {
        scrollTrigger: {
          trigger: figcaption,
          start: "top 90%",
          toggleActions: "play none none reverse"
        },
        y: 30,
        opacity: 0,
        duration: 0.6,
        stagger: 0.1,
        ease: "back.out(1.2)",
        delay: 1 // After testimonial text animation
      })
    }
  }

  disconnect() {
    if (this.turboListener) {
      document.removeEventListener('turbo:frame-load', this.turboListener)
    }
    ScrollTrigger.getAll().forEach(trigger => trigger.kill())
  }
}
